'use client'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { getAutocomplete } from '@/travelmde/actions/elasticSearch'

const SearchBar = ({ lang, title }) => {
  const router = useRouter()
  const [search, setSearch] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [typingTimeout, setTypingTimeout] = useState(null)

  const searchContainerRef = useRef(null)

  const howManySuggestions = 10

  const debouncedSearch = useCallback(
    (value) => {
      if (typingTimeout) {
        clearTimeout(typingTimeout)
      }

      setTypingTimeout(
        setTimeout(async () => {
          if (value.length > 2) {
            setIsLoading(true)
            try {
              const matches = await getAutocomplete(value, lang, howManySuggestions, true)
              const onlyTitle = matches.map((match) => match.title)
              setSuggestions(onlyTitle)
            } catch (error) {
              console.error('Search error:', error)
            } finally {
              setIsLoading(false)
            }
          } else {
            setSuggestions([])
          }
        }, 300),
      )
    },
    [lang, typingTimeout],
  )

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      router.push(`/${lang}/search/${search}/1`)
    }
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setSearch(value)
    debouncedSearch(value)
  }

  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout)
      }
    }
  }, [typingTimeout])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSuggestions([])
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const matchSelected = (suggestion) => {
    setSearch(suggestion) // Update the input value when suggestion is selected
    setSuggestions([]) // Clear suggestions
    router.push(`/${lang}/search/${suggestion}/1`)
  }

  return (
    <div className="search__bar" style={{ position: 'relative' }} ref={searchContainerRef}>
      <input
        type="text"
        placeholder={title}
        name="search"
        value={search}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-200"
        autoComplete="off"
      />
      <button onClick={() => handleKeyDown({ key: 'Enter' })}>
        {isLoading ? (
          <div className="animate-spin">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4.75V6.25"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.1266 6.87347L16.0659 7.93413"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M19.25 12H17.75"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.1266 17.1265L16.0659 16.0659"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 17.75V19.25"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.9342 16.0659L6.87354 17.1265"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.25 12H4.75"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.9342 7.93413L6.87354 6.87347"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_304_51)">
              <path
                d="M10.9031 18.1695V16.8164H3.34375V18.1695C3.34557 18.3849 3.29874 18.598 3.20676 18.7928C3.11477 18.9876 2.98 19.1591 2.8125 19.2945H3.41563V19.7164C3.41563 19.7918 3.44559 19.8642 3.49892 19.9175C3.55225 19.9708 3.62458 20.0008 3.7 20.0008H4.16562C4.24105 20.0008 4.31338 19.9708 4.36671 19.9175C4.42004 19.8642 4.45 19.7918 4.45 19.7164V19.2945H9.8125V19.7164C9.8125 19.7918 9.84246 19.8642 9.89579 19.9175C9.94912 19.9708 10.0215 20.0008 10.0969 20.0008H10.5594C10.6348 20.0008 10.7071 19.9708 10.7605 19.9175C10.8138 19.8642 10.8438 19.7918 10.8438 19.7164V19.2945H11.4469C11.2771 19.1603 11.1399 18.9894 11.0457 18.7945C10.9516 18.5996 10.9028 18.386 10.9031 18.1695Z"
                fill="#111111"
              />
              <path
                d="M3.0281 8.25703C3.02798 7.91025 2.94411 7.56862 2.7836 7.26121C2.6231 6.9538 2.39072 6.68971 2.10623 6.49141L2.08123 6.47266C1.72847 6.94337 1.5377 7.51569 1.53748 8.10391V18.3352C1.53876 18.5392 1.60483 18.7375 1.72616 18.9015C1.84748 19.0655 2.01778 19.1867 2.21248 19.2477C2.44742 19.1815 2.65429 19.0404 2.80155 18.8457C2.94881 18.6511 3.02837 18.4136 3.0281 18.1695V8.25703Z"
                fill="#111111"
              />
              <path
                d="M3.34373 8.25625V16.4969H10.9031V11.25C10.9016 11.2438 10.9016 11.2374 10.9031 11.2312C10.4571 10.9026 10.0912 10.4772 9.83287 9.98699C9.57458 9.49681 9.43056 8.95452 9.41162 8.40077C9.39268 7.84702 9.49931 7.29616 9.72351 6.78947C9.94771 6.28278 10.2837 5.8334 10.7062 5.475C10.4648 5.40346 10.2143 5.36766 9.96248 5.36875H9.54998V1.5625H9.86248V0.3125C9.86248 0.22962 9.82955 0.150134 9.77095 0.0915291C9.71234 0.032924 9.63286 0 9.54998 0L4.70935 0C4.62647 0 4.54698 0.032924 4.48838 0.0915291C4.42977 0.150134 4.39685 0.22962 4.39685 0.3125V1.5625H4.70935V5.35938H5.14373V1.5625Z"
                fill="#111111"
              />
              <path
                d="M11.2219 16.6496V18.1684C11.2235 18.4115 11.3034 18.6477 11.4497 18.8419C11.5961 19.0361 11.8011 19.178 12.0344 19.2465C12.2294 19.186 12.3999 19.0649 12.5213 18.9008C12.6427 18.7367 12.7086 18.5381 12.7094 18.334V11.9246C12.1837 11.8678 11.6763 11.6983 11.2219 11.4277V16.6496Z"
                fill="#111111"
              />
              <path
                d="M18.3062 12.3435L16.4312 10.4685L16.1187 10.781L15.6968 10.3622C16.1863 9.76487 16.4526 9.01576 16.45 8.24348C16.4511 7.4814 16.1932 6.74157 15.7184 6.14544C15.2436 5.54932 14.5803 5.13233 13.8373 4.96294C13.0943 4.79356 12.3157 4.88183 11.6295 5.21328C10.9433 5.54473 10.3901 6.09965 10.0609 6.78694C9.73165 7.47423 9.64588 8.25304 9.81766 8.9955C9.98943 9.73797 10.4085 10.4 11.0062 10.8728C11.6038 11.3457 12.3445 11.6012 13.1066 11.5976C13.8686 11.594 14.6068 11.3314 15.2 10.8529L15.625 11.2935L15.3125 11.606L17.1875 13.481C17.2362 13.5299 17.2942 13.5688 17.358 13.5953C17.4218 13.6217 17.4902 13.6354 17.5593 13.6354C17.6284 13.6354 17.6968 13.6217 17.7607 13.5953C17.8245 13.5688 17.8824 13.5299 17.9312 13.481L18.325 13.0841C18.4204 12.9833 18.472 12.8487 18.4685 12.7099C18.465 12.5711 18.4066 12.4394 18.3062 12.3435ZM13.0906 10.6685C12.6151 10.6691 12.1502 10.5287 11.7546 10.265C11.359 10.0013 11.0505 9.62621 10.8682 9.18715C10.6858 8.7481 10.6377 8.26481 10.7301 7.79844C10.8225 7.33208 11.0511 6.90359 11.3871 6.56719C11.723 6.2308 12.1512 6.00161 12.6175 5.90863C13.0837 5.81565 13.5671 5.86306 14.0064 6.04485C14.4457 6.22665 14.8212 6.53466 15.0854 6.92992C15.3496 7.32518 15.4906 7.78992 15.4906 8.26535C15.491 8.58079 15.4292 8.89321 15.3088 9.18475C15.1884 9.47629 15.0116 9.74124 14.7887 9.96443C14.5658 10.1876 14.3011 10.3647 14.0097 10.4855C13.7184 10.6063 13.406 10.6685 13.0906 10.6685Z"
                fill="#111111"
              />
            </g>
            <defs>
              <clipPath id="clip0_304_51">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        )}
      </button>
      {suggestions.length > 0 && (
        <div
          className="absolute w-full mt-1 bg-white border rounded-md shadow-lg z-10"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: '#FFF5E6',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className="px-4 py-2 cursor-pointer hover:font-bold"
              onClick={() => matchSelected(suggestion)}
            >
              {suggestion.slice(0, 13)} {suggestion.length > 13 && '...'}
            </div>
          ))}
          <div
            className="px-4 py-2 cursor-pointer hover:bg-gray-100 border-t flex items-center"
            onClick={() => handleKeyDown({ key: 'Enter' })}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2"
            >
              <path
                d="M19.361 18.2168L14.601 13.2662C15.8249 11.8113 16.4954 9.98069 16.4954 8.07499C16.4954 3.62251 12.8729 0 8.42045 0C3.96797 0 0.345459 3.62251 0.345459 8.07499C0.345459 12.5275 3.96797 16.15 8.42045 16.15C10.092 16.15 11.6849 15.6458 13.0467 14.6888L17.8429 19.677C18.0434 19.8852 18.313 20 18.602 20C18.8755 20 19.1349 19.8957 19.3319 19.7061C19.7504 19.3034 19.7637 18.6357 19.361 18.2168ZM8.42045 2.10652C11.7115 2.10652 14.3889 4.78391 14.3889 8.07499C14.3889 11.3661 11.7115 14.0435 8.42045 14.0435C5.12937 14.0435 2.45198 11.3661 2.45198 8.07499C2.45198 4.78391 5.12937 2.10652 8.42045 2.10652Z"
                fill="currentColor"
              />
            </svg>
            <span>
              {title} {search}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchBar
